import React, { ReactNode, createContext, useContext } from 'react';
import { renpyApi } from '../../services/renpyApiService';
import { RenpyApiService } from '../../services/renpyApiService';

/**
 * Context for providing RenpyApiService throughout the application.
 * 
 * This context follows the same pattern as ApiServiceContext but is
 * specifically configured for the RenPy API subdomain.
 */
export const RenpyApiServiceContext = createContext<RenpyApiService>(
  new RenpyApiService(renpyApi)
);

/**
 * Provider component for RenpyApiService.
 * 
 * Wraps child components to provide access to the RenpyApiService
 * through the useRenpyApiService hook.
 * 
 * @param children - React components to wrap with the provider
 * 
 * @example
 * ```tsx
 * <RenpyApiServiceProvider>
 *   <YourComponent />
 * </RenpyApiServiceProvider>
 * ```
 */
export function RenpyApiServiceProvider({ children }: { children: ReactNode }) {
  const renpyApiService = useContext(RenpyApiServiceContext);

  return (
    <RenpyApiServiceContext.Provider value={renpyApiService}>
      {children}
    </RenpyApiServiceContext.Provider>
  );
}

/**
 * Hook to access the RenpyApiService from context.
 * 
 * Must be used within a RenpyApiServiceProvider.
 * 
 * @returns RenpyApiService instance
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const renpyApiService = useRenpyApiService();
 *   
 *   const handleUpload = async (file: File) => {
 *     const formData = new FormData();
 *     formData.append('gameFile', file);
 *     
 *     const result = await renpyApiService.uploadRenpyGame(
 *       bookUuid,
 *       episodeUuid,
 *       revision,
 *       formData
 *     );
 *   };
 *   
 *   return <button onClick={handleUpload}>Upload</button>;
 * }
 * ```
 */
export const useRenpyApiService = () => useContext(RenpyApiServiceContext);

